import { cn } from '@/lib/utils';

import type { FeedOutcomeTone, FeedStakeTone } from './feedRowView';

// The row's grid, shared by the frame's header so the icon never moves as a row opens. A row
// sits outside the card's frame, so it turns the card's scale on itself
export const FEED_ROW_GRID =
  'ac-scale relative grid min-h-[var(--ac-row-h)] grid-cols-[var(--ac-row-icon)_minmax(0,1fr)_auto] items-center gap-x-[var(--ac-row-gap)] px-[var(--ac-row-px)] py-[var(--ac-row-py)]';

export const FEED_ROW_SURFACE_CLASS =
  'group/feed-row items-start rounded-[var(--ac-frame-radius)] border border-transparent transition-colors duration-[120ms] has-[:focus-visible]:bg-surface-hover has-[[data-state=open]]:bg-surface-hover motion-reduce:transition-none';

export const FEED_ROW_AGE_CLASS =
  'shrink-0 whitespace-nowrap text-[length:calc(11.5*var(--ac-u))] text-foreground-muted opacity-55';

export const FEED_ROW_TRAY_CLASS =
  'pointer-events-none absolute inset-y-0 right-0 z-[1] flex items-center gap-[calc(2*var(--ac-u))] rounded-r-[var(--ac-frame-radius)] bg-[linear-gradient(to_right,transparent,var(--surface-hover)_calc(32*var(--ac-u)))] pl-[calc(40*var(--ac-u))] pr-[calc(8*var(--ac-u))] opacity-0 [&_button]:pointer-events-none group-has-[:focus-visible]/feed-row:opacity-100 group-has-[:focus-visible]/feed-row:[&_button]:pointer-events-auto group-hover/feed-row:opacity-100 group-hover/feed-row:[&_button]:pointer-events-auto group-hover/feed-row:transition-opacity group-hover/feed-row:duration-[120ms] has-[[data-state=open]]:opacity-100 [&_button]:has-[[data-state=open]]:pointer-events-auto motion-reduce:transition-none';

// The row's first line, which the open frame's header repeats in place
export const FEED_ROW_LINE_CLASS =
  'flex h-[var(--ac-row-line-h)] min-w-0 items-center gap-[var(--ac-gap-sm)]';

export const FEED_ROW_META_CLASS =
  'min-w-0 shrink-[100] truncate text-[length:calc(12*var(--ac-u))] text-foreground-muted';

const FEED_ROW_TITLE_TYPE =
  'min-w-0 truncate text-[length:var(--ac-row-title)] font-medium leading-[var(--ac-row-title-lh)] text-foreground';

export const FEED_ROW_TITLE_CLASS = `${FEED_ROW_TITLE_TYPE} text-left outline-none after:absolute after:inset-0 after:rounded-[var(--ac-frame-radius)] after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring`;

export const FEED_STATIC_ROW_TITLE_CLASS = FEED_ROW_TITLE_TYPE;

export const FEED_STRUCK_TITLE_CLASS =
  'text-foreground-muted line-through decoration-border-strongest';

export const FEED_ROW_SLOT_CLASS = {
  home: 'ac-scale -mx-[calc(var(--ac-row-px)_+_1px)]',
  feed: 'ac-scale -mx-[min(calc(var(--ac-row-px)_+_1px),max(0px,(100cqw_-_48rem)/2))]',
} as const;

export const FEED_ROW_SLOT_PADDING = {
  expanded: 'py-2',
  comfortable: 'py-1.5',
  compact: 'py-px',
} as const;

export const FEED_ACTION_BUTTON_CLASS =
  'flex shrink-0 items-center justify-center whitespace-nowrap rounded-full text-foreground-subtle focus-ring-inset transition-colors duration-[120ms] hover:bg-secondary-hover hover:text-foreground focus-visible:bg-secondary-hover data-[state=open]:bg-secondary-hover data-[state=open]:text-foreground motion-reduce:transition-none';

// "Open in" is the app's mark at the open card's band's far end, quiet until pointed at; the card's other sources match
// it
export const FEED_OPEN_LINK_CLASS =
  'relative z-[1] flex h-[calc(28*var(--ac-u))] min-w-0 shrink items-center gap-[calc(6*var(--ac-u))] whitespace-nowrap rounded-full px-[calc(8*var(--ac-u))] text-[length:calc(12.5*var(--ac-u))] font-medium text-foreground-subtle focus-ring transition-colors duration-[120ms] hover:bg-secondary-hover hover:text-foreground motion-reduce:transition-none';

export const FEED_CTA_CLASS =
  'pointer-events-auto flex h-[calc(28*var(--ac-u))] items-center gap-[calc(6*var(--ac-u))] rounded-full border border-border-strong px-[calc(11*var(--ac-u))] text-[length:calc(12.5*var(--ac-u))] font-medium text-foreground focus-ring transition-colors duration-[120ms] hover:bg-secondary-hover focus-visible:bg-secondary-hover motion-reduce:transition-none';

export const FEED_TRAY_CTA_CLASS = cn(
  FEED_CTA_CLASS,
  'border-border-strongest bg-background'
);

export const FEED_HOVER_REVEAL_CLASS =
  'opacity-0 transition-opacity duration-[120ms] motion-reduce:transition-none [&_button]:pointer-events-none group-hover/feed-row:opacity-100 group-hover/feed-row:[&_button]:pointer-events-auto group-has-[:focus-visible]/feed-row:opacity-100 group-has-[:focus-visible]/feed-row:[&_button]:pointer-events-auto';

export const FEED_ROW_END_CTA_CLASS =
  'mr-[calc(var(--ac-row-end-inset)_-_11*var(--ac-u)_-_1px)]';

export const FEED_PILL_CLASS =
  'h-[var(--ac-row-pill-h)] rounded-full bg-surface px-[var(--ac-row-pill-px)] text-[length:var(--ac-row-pill)] font-medium leading-[var(--ac-row-pill-lh)] shadow-none';

export const FEED_CHIP_CLASS =
  'shrink-0 rounded-[calc(7*var(--ac-u))] px-[calc(8*var(--ac-u))] py-[calc(2*var(--ac-u))] text-[length:calc(11.5*var(--ac-u))] font-medium leading-[calc(16*var(--ac-u))]';

export const FEED_FADED_TEXT_CLASS =
  'text-[color-mix(in_srgb,var(--foreground-muted)_75%,transparent)]';

export const FEED_DESCRIPTION_CLASS =
  'line-clamp-2 min-w-0 text-[length:calc(12.5*var(--ac-u))] leading-[calc(18*var(--ac-u))] text-foreground-muted';

export const OUTCOME_TEXT_CLASS: Record<FeedOutcomeTone, string> = {
  done: 'text-kind-done',
  danger: 'text-danger-11',
  muted: 'text-foreground-muted',
};

export const STAKE_PILL_CLASS: Record<FeedStakeTone, string> = {
  muted: 'bg-secondary text-foreground-subtle',
  warn: 'bg-warning-3 text-warning-11',
  danger: 'bg-danger-3 text-danger-11',
};
